import { useEffect, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { format, subDays } from 'date-fns';
import api from 'api';
import { AnalyticsDeletedUsersWarning } from 'components/analytics';
import { Box } from 'components/common/box';
import { DateRangeSelector, LineChart } from 'components/common/charts';
import { RadioGroup } from 'components/common/form';
import { StatItem } from 'components/common/stats';

type AccessEntry = {
  labels: string[];
  deletedUsers: number[];
  validUsers: number[];
  totalCount: number;
};

const VisitorsChart = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const [courseAccessEntries, setCourseAccessEntries] =
    useState<AccessEntry>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(
    `${format(subDays(dateNow, 30), 'y-MM-dd')}T00:00:00`
  );
  const [toDate, setToDate] = useState(
    `${format(dateNow, 'y-MM-dd')}T23:59:59`
  );

  const handleDateChange = ({ fromDate, toDate }) => {
    setFromDate(fromDate);
    setToDate(toDate);
  };

  useEffect(() => {
    updateData();
  }, [includeDeleted, fromDate, toDate]);

  const updateData = async () => {
    try {
      setLoading(true);

      const entries = await api.get(
        `/admin/courses/${course.id}/analytics/course-access` +
          `?fromDate=${fromDate}` +
          `&toDate=${toDate}` +
          `&includeDeleted=${includeDeleted}`
      );
      setCourseAccessEntries(entries.data.entries);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const data = courseAccessEntries?.labels
    ? courseAccessEntries.labels.map((label, i) => ({
        label,
        [messages.analyticsUsersInTotal]:
          courseAccessEntries.validUsers[i] +
          courseAccessEntries.deletedUsers[i],
        [includeDeleted
          ? messages.analyticsExistingAccounts
          : messages.commonUsers]: courseAccessEntries.validUsers[i],
        [messages.analyticsDeletedAccounts]: courseAccessEntries.deletedUsers[i]
      }))
    : null;

  return (
    <div className="chart">
      <Box
        headerContent={
          <DateRangeSelector size="header" updateData={handleDateChange} />
        }
        loading={loading}
        title={messages.analyticsVisitorCount}
      >
        <div className="total-users">
          <StatItem
            align="left"
            horizontal={true}
            label={messages.analyticsUsersInTotal}
            value={courseAccessEntries?.totalCount}
          />

          <RadioGroup
            field="includeDeleted"
            id="include-deleted"
            initialValue={includeDeleted}
            inline={true}
            onChange={(value) => setIncludeDeleted(value)}
            options={[
              {
                id: 'active',
                label: messages.analyticsAccountsActive,
                value: false
              },
              {
                id: 'deleted',
                label: messages.analyticsAccountsDeleted,
                value: true
              }
            ]}
          />
        </div>

        <AnalyticsDeletedUsersWarning
          date={fromDate}
          style={{ margin: '10px 15px' }}
          visible={includeDeleted}
        />

        <div className="chart">
          <LineChart
            data={data}
            xAxisDataKey="label"
            {...(includeDeleted
              ? {
                  lineColors: [
                    theme.chartMainColor,
                    theme.textColorLighter,
                    theme.dangerColor
                  ],
                  yAxisDataKey: [
                    messages.analyticsUsersInTotal,
                    messages.analyticsExistingAccounts,
                    messages.analyticsDeletedAccounts
                  ]
                }
              : { yAxisDataKey: messages.commonUsers })}
          />
        </div>
      </Box>

      <style jsx>{`
        .total-users {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          padding: 10px 5px;
        }
        .chart {
          padding: 0 5px;
        }
      `}</style>
    </div>
  );
};

export default VisitorsChart;
